/** Which meter was this for — a person's choice, offered only where it can matter (ADR-0098, ADR-0099).
 *
 * The structure workbook can declare a stream for one **meter**, and the census counts a bill
 * against such a stream only when the figure names that meter. A bill prints its meter as a
 * string, and SEC-05 says nothing a document says is resolved to a row, so the only honest way to
 * say "this was the main incomer" is for the reviewer to say it.
 *
 * Four rules, each asserted in `ReviewMeterChoice.test.tsx`, `EvidenceManualMeterChoice.test.tsx`
 * and `ImportMeterChoice.test.tsx`:
 *
 * - **Rendered only when the site has something to choose between.** The list is the site's
 *   meters that carry a meter-keyed stream for this kind of document; for every site whose
 *   streams are facility-level it is empty and this renders nothing at all. A control that cannot
 *   change the census is noise on the screen where a figure enters the inventory.
 * - **Optional and never pre-selected.** A default would be a guess wearing a default's clothes,
 *   accepted by whoever was in a hurry. Leaving it blank is an honest answer: nothing is guessed
 *   from the document, and (ADR-0098 amendment, D-27) a record with no meter counts only when it
 *   is the one stream for its carrier at the site; with two or more the stream keeps showing the
 *   month as missing until a meter is chosen.
 * - **Never offers what the document printed.** The options are the registered meters' own labels.
 *   The reference on the bill is not matched, suggested or highlighted.
 * - **A failed read shows no picker**, not an empty one and not an error banner: the choice is
 *   optional, so the worst a failure costs is a gap that stays visible.
 *
 * `MeterSelect` is the one presentational control. Four screens use it — a bill under review
 * (`MeterPicker`, below), a hand-typed figure (`ManualMeterPicker`), the groups of an imported
 * file (`Import.tsx`) and the new rows of a returned spreadsheet (`Bulk.tsx`, ADR-0103) — so the
 * rules above hold in one place and cannot drift per screen. Each caller supplies the meters it
 * read; none supplies a value of its own.
 *
 * **One difference, deliberate:** on the first three, leaving the choice blank is an answer. On the
 * spreadsheet it is not - the apply is *held* until every group is answered, because a bulk apply
 * is read once and quickly and a silent "no meter" is how a stream goes on showing a month as
 * missing. That screen therefore passes `noneLabel`, which adds an explicit "none of these" so the
 * hold can always be satisfied, and `unansweredLabel`, so the empty option does not say "Not saying".
 */

import { useEffect } from "react";

import { useMeterChoiceOffer } from "../api/evidence";
import type { MeterChoice } from "../api/evidence";
import { useManualMeterChoices } from "../api/queries";
import styles from "./Evidence.module.css";
import { NONE_OF_THESE } from "./meterGroup";

/** The control itself. Renders nothing for an empty list, and never selects anything. */
export function MeterSelect({
  meters,
  value,
  onChange,
  name,
  label = "Which meter was this bill for? (optional)",
  note = "This site expects bills from particular meters. Say which one this is and it counts towards that meter\u2019s stream. A bill with no meter counts when it is the only stream for that carrier at the site; otherwise choose the meter.",
  unansweredLabel = "Not saying",
  noneLabel,
}: {
  meters: readonly MeterChoice[];
  value: string;
  onChange: (meterId: string) => void;
  /** The accessible name, which distinguishes two pickers on one screen. */
  name: string;
  label?: string;
  note?: string;
  /** What the empty option says. The default is for a question that is *optional*; a question that
   * is **held** until answered (the spreadsheet round trip, ADR-0103) says "Choose a meter", because
   * there "not saying" is not an available answer. */
  unansweredLabel?: string;
  /** Offers an explicit "none of these" as its own option, value `NONE_OF_THESE`. Present only where
   * the empty option is not an answer, so a held question can always be satisfied without a guess. */
  noneLabel?: string;
}) {
  if (meters.length === 0) return null;

  return (
    <>
      <label className={styles.field}>
        <span>{label}</span>
        <select aria-label={name} value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">{unansweredLabel}</option>
          {meters.map((meter) => (
            <option key={meter.meter_id} value={meter.meter_id}>
              {meter.label || `Unlabelled ${meter.kind.replace(/_/g, " ")} meter`}
              {meter.retired ? " (retired)" : ""}
            </option>
          ))}
          {noneLabel ? <option value={NONE_OF_THESE}>{noneLabel}</option> : null}
        </select>
      </label>
      {note ? <p className={styles.note}>{note}</p> : null}
    </>
  );
}

export function MeterPicker({
  documentId,
  facilityId,
  fuelUse,
  value,
  onChange,
  name,
  remember,
  onRememberChange,
}: {
  documentId: string;
  facilityId: string;
  fuelUse: "mobile" | "stationary" | undefined;
  value: string;
  onChange: (meterId: string) => void;
  /** Distinguishes two pickers on one screen (the first site and "record this site too"). */
  name: string;
  /** Whether the reviewer has ticked *remember* (ADR-0107). Supplied only by a screen that sends it. */
  remember?: boolean;
  onRememberChange?: (remember: boolean) => void;
}) {
  const offer = useMeterChoiceOffer(documentId, facilityId, fuelUse, true);
  const meters = offer.data?.meters ?? [];
  const savedId = offer.data?.suggested_meter_id ?? null;
  const saved = savedId ? meters.find((meter) => meter.meter_id === savedId) : undefined;
  //: **A choice the list no longer holds is not an answer** (EXP-35, ADR-0112). A draft restored from
  //: an earlier visit can name a meter that has since been retired or removed from this site; a
  //: controlled `<select>` would then show the first option while the screen still sent the stale
  //: id. Once the offer has loaded, anything not in it is cleared.
  const loaded = offer.data !== undefined;
  const known = meters.some((meter) => meter.meter_id === value);
  useEffect(() => {
    if (loaded && value !== "" && !known) onChange("");
  }, [loaded, value, known, onChange]);
  const savedName = saved
    ? saved.label || `Unlabelled ${saved.kind.replace(/_/g, " ")} meter`
    : "";
  return (
    <>
      <MeterSelect meters={meters} value={value} onChange={onChange} name={name} />
      {/* **ADR-0107. A saved answer is offered, never applied.** A person once said that bills
          printing this bill's meter reference are for this meter; pressing the button is the
          reviewer choosing it, exactly as choosing from the list is. Nothing is pre-selected, and
          the printed reference is neither shown nor compared here - the server found the saved row
          and named only the meter. */}
      {saved && value !== saved.meter_id ? (
        <p className={styles.note} data-testid="saved-meter-suggestion">
          A reviewer earlier saved that bills with this meter reference are for{" "}
          <strong>{savedName}</strong>. It is not chosen for you.{" "}
          <button type="button" className={styles.secondary} onClick={() => onChange(saved.meter_id)}>
            Use {savedName}
          </button>
        </p>
      ) : null}
      {onRememberChange && value && offer.data?.rememberable ? (
        <div className={styles.field} data-testid="remember-meter-reference">
          <label>
            <input
              type="checkbox"
              checked={remember === true}
              onChange={(event) => onRememberChange(event.target.checked)}
            />{" "}
            Remember this for next time
          </label>
          <p className={styles.note}>
            Saves that bills with this meter reference are for the meter you chose. The next bill
            with the same reference will be offered that meter, never given it, and if the meter is
            retired or replaced you will be asked to look again.
          </p>
        </div>
      ) : null}
    </>
  );
}

/** The same question for a figure a person types in with no document (ADR-0099).
 *
 * Asked of the registry for the site, the kind of activity and — once chosen — the fuel, because a
 * stream is matched on all three: a meter whose only stream is for electricity cannot be satisfied
 * by diesel, so it is not offered for it. Nothing the person typed into the basis is read.
 */
export function ManualMeterPicker({
  facilityId,
  category,
  carrier,
  value,
  onChange,
}: {
  facilityId: string;
  category: string;
  carrier: string;
  value: string;
  onChange: (meterId: string) => void;
}) {
  const choices = useManualMeterChoices(facilityId, category, carrier, true);
  return (
    <MeterSelect
      meters={choices.data ?? []}
      value={value}
      onChange={onChange}
      name="Which meter was this reading for?"
      label="Which meter was this reading for? (optional)"
      note="This site expects figures from particular meters. Say which one this came off and it counts towards that meter’s stream. A figure with no meter counts when it is the only stream for that carrier at the site; otherwise choose the meter."
    />
  );
}
